<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageApi from 'dashboard/api/inbox/message';
import { useEnvioSegurado } from 'dashboard/components-next/message/useEnvioSegurado';
import { computed } from 'vue';

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    // O aviso da janela de 15 minutos e a menção ao WhatsApp só fazem sentido
    // em caixa de WhatsApp — numa caixa web a edição é local e sem prazo.
    isWhatsAppInbox: {
      type: Boolean,
      default: false,
    },
    // WhatsApp oficial (WAME): a API não apaga nem edita mensagem que já saiu (20/10/2026). Separado de
    // `isWhatsAppInbox`, que é das conexões não oficiais (lá a edição funciona).
    isWhatsAppOficial: {
      type: Boolean,
      default: false,
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo', 'forward', 'select'],
  setup(props) {
    const { getPlainText } = useMessageFormatter();
    // Ainda esperando para sair (desfazer envio, 20/10/2026)? Então o "Excluir" vira "Desfazer envio".
    const { emEspera } = useEnvioSegurado(
      computed(
        () =>
          props.message.content_attributes ?? props.message.contentAttributes
      )
    );

    return {
      getPlainText,
      emEspera,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      apagando: false,
      showEditModal: false,
      editContent: '',
      isEditing: false,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
    // 20/10/2026 (caso real: mensagem mandada na conversa errada): apagar no advup NÃO apaga no WhatsApp de quem
    // recebeu — a API oficial não permite. Mensagem do escritório em caixa de WhatsApp: a confirmação diz isso e
    // ensina a apagar para todos pelo celular do escritório.
    apagaSoAqui() {
      const tipo = this.message.message_type ?? this.message.messageType;
      return this.isWhatsAppOficial && tipo === 1;
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      // Sem `catch`, a tradução que falhava não dizia nada: a pessoa ficava
      // esperando um texto que não vinha.
      this.$store
        .dispatch('translateMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          targetLanguage,
        })
        .catch(() =>
          useAlert(this.$t('CONVERSATION.CONTEXT_MENU.TRANSLATE_ERROR'))
        );
      useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    handleForward() {
      this.$emit('forward', this.message);
      this.handleClose();
    },
    handleSelect() {
      this.$emit('select', this.message);
      this.handleClose();
    },
    openEditModal() {
      this.editContent = this.messageContent;
      this.handleClose();
      this.showEditModal = true;
    },
    async confirmEdit() {
      if (
        !this.editContent.trim() ||
        this.editContent === this.messageContent
      ) {
        this.showEditModal = false;
        return;
      }
      this.isEditing = true;
      try {
        await this.$store.dispatch('editMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          content: this.editContent,
        });
        useAlert(this.$t('CONVERSATION.EDIT_MESSAGE.SUCCESS'));
        this.showEditModal = false;
      } catch (error) {
        // advup — achado 2026-07-20: `error` aqui é o erro do Axios, cujo
        // `.message` é sempre genérico ("Request failed with status code
        // 422") — o motivo real que o backend manda (janela de 15min
        // expirada, "aguarde confirmação de envio", erro da Evolution API)
        // ficava escondido, então toda falha de edição parecia a MESMA
        // falha misteriosa em vez de mostrar por que travou dessa vez.
        const msg =
          error?.response?.data?.error ||
          error?.message ||
          this.$t('CONVERSATION.EDIT_MESSAGE.ERROR');
        useAlert(msg);
      } finally {
        this.isEditing = false;
      }
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    openDeleteModal() {
      this.handleClose();
      this.showDeleteModal = true;
    },
    async desfazerEnvio() {
      if (this.apagando) return;
      this.apagando = true;
      try {
        const { data } = await MessageApi.desfazerEnvio(
          this.conversationId,
          this.messageId
        );
        if (data?.id) this.$store.dispatch('updateMessage', data);
        useAlert(this.$t('CONVERSATION.DESFAZER_ENVIO.DESFEITO'));
        this.showDeleteModal = false;
        this.handleClose();
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            this.$t('CONVERSATION.DESFAZER_ENVIO.JA_SAIU')
        );
      } finally {
        this.apagando = false;
      }
    },
    async confirmDeletion() {
      if (this.apagando) return;
      this.apagando = true;
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.showDeleteModal = false;
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      } finally {
        this.apagando = false;
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Editar mensagem: casca do produto (`.advup-modal`), topo azul. Era um
         cabeçalho e um rodapé escritos à mão dentro do modal antigo. -->
    <Teleport to="body">
      <Transition name="advup-saida">
        <div
          v-if="showEditModal && enabledOptions['edit']"
          class="advup-modal-fundo"
          @click.self="closeEditModal"
        >
          <div
            v-modal-acessivel="closeEditModal"
            class="advup-modal advup-modal--teto mcm-editar"
          >
            <div class="advup-modal__topo">
              <span class="advup-modal__selo" aria-hidden="true">
                <i class="i-lucide-pencil-line" />
              </span>
              <div class="min-w-0 flex-1">
                <h2 class="advup-modal__titulo">
                  {{ $t('CONVERSATION.EDIT_MESSAGE.TITLE') }}
                </h2>
                <p class="advup-modal__sub advup-modal__sub--solta">
                  {{
                    isWhatsAppInbox
                      ? $t('CONVERSATION.EDIT_MESSAGE.SUBTITLE_WHATSAPP')
                      : $t('CONVERSATION.EDIT_MESSAGE.SUBTITLE')
                  }}
                </p>
              </div>
              <button
                type="button"
                class="advup-modal__fechar"
                :aria-label="$t('CONVERSATION.EDIT_MESSAGE.CANCEL')"
                @click="closeEditModal"
              >
                <i class="i-lucide-x" />
              </button>
            </div>

            <div class="advup-modal__corpo mcm-corpo">
              <div class="mcm-rotulo">
                <label for="edit-message-content" class="advup-rotulo">
                  {{ $t('CONVERSATION.EDIT_MESSAGE.FIELD_LABEL') }}
                </label>
                <span class="mcm-conta">{{ editContent.length }}</span>
              </div>
              <textarea
                id="edit-message-content"
                v-model="editContent"
                rows="7"
                class="advup-campo advup-campo--area mcm-texto"
                @keydown.meta.enter="confirmEdit"
                @keydown.ctrl.enter="confirmEdit"
              />
              <!-- A janela de 15 min é do WhatsApp, não nossa. Dizer isso na
                   hora da edição evita a pergunta "por que não deixou?" depois. -->
              <p v-if="isWhatsAppInbox" class="mcm-janela">
                <i class="i-lucide-clock mcm-janela__ico" aria-hidden="true" />
                {{ $t('CONVERSATION.EDIT_MESSAGE.WINDOW_HINT') }}
              </p>
            </div>

            <div class="advup-modal__rodape advup-modal__rodape--acoes">
              <span class="mcm-atalho">
                {{ $t('CONVERSATION.EDIT_MESSAGE.SHORTCUT_HINT') }}
              </span>
              <span class="advup-modal__espaco" />
              <button
                type="button"
                class="advup-btn advup-btn--vazado"
                @click="closeEditModal"
              >
                {{ $t('CONVERSATION.EDIT_MESSAGE.CANCEL') }}
              </button>
              <button
                type="button"
                class="advup-btn-forte"
                :disabled="
                  isEditing ||
                  !editContent.trim() ||
                  editContent === messageContent
                "
                @click="confirmEdit"
              >
                <i
                  :class="
                    isEditing
                      ? 'i-lucide-loader-2 animate-spin'
                      : 'i-lucide-check'
                  "
                  aria-hidden="true"
                />
                {{ $t('CONVERSATION.EDIT_MESSAGE.SAVE') }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
    <!-- Confirmar exclusão: o padrão de confirmação do produto — modal estreito
         com topo RUBI, porque apaga. Era o `woot-delete-modal`.
         20/10/2026: no WhatsApp oficial, a mensagem do escritório que já saiu NÃO se apaga por aqui (a API oficial não
         deixa) — o modal explica e ensina o celular; enquanto ela ainda espera, oferece desfazer o envio. -->
    <Teleport to="body">
      <Transition name="advup-saida">
        <div
          v-if="showDeleteModal && enabledOptions['delete']"
          class="advup-modal-fundo"
          @click.self="closeDeleteModal"
        >
          <div
            v-modal-acessivel="closeDeleteModal"
            role="alertdialog"
            class="advup-modal advup-modal--estreito"
          >
            <!-- 1. Ainda não saiu: desfazer. -->
            <template v-if="apagaSoAqui && emEspera">
              <div class="advup-modal__topo">
                <span class="advup-modal__selo" aria-hidden="true">
                  <i class="i-lucide-undo-2" />
                </span>
                <div class="min-w-0 flex-1">
                  <h2 class="advup-modal__titulo advup-modal__titulo--solto">
                    {{
                      $t(
                        'CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE_EM_ESPERA'
                      )
                    }}
                  </h2>
                </div>
              </div>
              <div class="advup-modal__corpo">
                <p class="advup-nota">
                  {{
                    $t(
                      'CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE_EM_ESPERA'
                    )
                  }}
                </p>
              </div>
              <div class="advup-modal__rodape advup-modal__rodape--acoes">
                <button
                  type="button"
                  class="advup-btn advup-btn--vazado"
                  @click="closeDeleteModal"
                >
                  {{
                    $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')
                  }}
                </button>
                <button
                  type="button"
                  class="advup-btn-forte reset-base"
                  :disabled="apagando"
                  @click="desfazerEnvio"
                >
                  <i
                    :class="
                      apagando
                        ? 'i-lucide-loader-2 animate-spin'
                        : 'i-lucide-undo-2'
                    "
                    aria-hidden="true"
                  />
                  {{ $t('CONVERSATION.DESFAZER_ENVIO.BOTAO') }}
                </button>
              </div>
            </template>

            <!-- 2. Já está no WhatsApp do cliente: não dá por aqui — o celular. -->
            <template v-else-if="apagaSoAqui">
              <div class="advup-modal__topo advup-modal__topo--ambar">
                <span class="advup-modal__selo" aria-hidden="true">
                  <i class="i-lucide-smartphone" />
                </span>
                <div class="min-w-0 flex-1">
                  <h2 class="advup-modal__titulo advup-modal__titulo--solto">
                    {{
                      $t(
                        'CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE_SO_AQUI'
                      )
                    }}
                  </h2>
                </div>
              </div>
              <div class="advup-modal__corpo flex flex-col gap-3">
                <p class="advup-nota">
                  {{
                    $t(
                      'CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE_SO_AQUI'
                    )
                  }}
                </p>
                <ol class="advup-nota list-decimal ps-5 flex flex-col gap-1">
                  <li>{{ $t('CONVERSATION.NOVIDADE_DESFAZER.CELULAR1') }}</li>
                  <li>{{ $t('CONVERSATION.NOVIDADE_DESFAZER.CELULAR2') }}</li>
                  <li>{{ $t('CONVERSATION.NOVIDADE_DESFAZER.CELULAR3') }}</li>
                </ol>
                <p class="advup-nota">
                  {{
                    $t(
                      'CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.COMO_APAGAR_PARA_TODOS'
                    )
                  }}
                </p>
              </div>
              <div class="advup-modal__rodape advup-modal__rodape--acoes">
                <button
                  type="button"
                  class="advup-btn-forte reset-base"
                  @click="closeDeleteModal"
                >
                  <i class="i-lucide-check" aria-hidden="true" />
                  {{ $t('CONVERSATION.NOVIDADE_DESFAZER.ENTENDI') }}
                </button>
              </div>
            </template>

            <!-- 3. Os demais: a confirmação de sempre. -->
            <template v-else>
              <div class="advup-modal__topo advup-modal__topo--rubi">
                <span class="advup-modal__selo" aria-hidden="true">
                  <i class="i-lucide-trash-2" />
                </span>
                <div class="min-w-0 flex-1">
                  <!-- A pergunta é longa: em uma linha ela saía cortada, e o que
                       sumia era justamente o "esta mensagem". -->
                  <h2 class="advup-modal__titulo advup-modal__titulo--solto">
                    {{
                      $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')
                    }}
                  </h2>
                </div>
              </div>
              <div class="advup-modal__corpo">
                <p class="advup-nota">
                  {{
                    $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')
                  }}
                </p>
              </div>
              <div class="advup-modal__rodape advup-modal__rodape--acoes">
                <button
                  type="button"
                  class="advup-btn advup-btn--vazado"
                  @click="closeDeleteModal"
                >
                  {{
                    $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')
                  }}
                </button>
                <button
                  type="button"
                  class="advup-btn advup-btn--perigo"
                  :disabled="apagando"
                  @click="confirmDeletion"
                >
                  <i
                    :class="
                      apagando
                        ? 'i-lucide-loader-2 animate-spin'
                        : 'i-lucide-trash-2'
                    "
                    aria-hidden="true"
                  />
                  {{
                    $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')
                  }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </Teleport>
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-ellipsis-vertical"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <!-- Entra como o menu do cartão da lista (24/10/2026, `.advup-pop`): era o único menu
           de contexto do produto que aparecia seco. -->
      <div class="advup-menu advup-pop mcm-menu" role="menu">
        <MenuItem
          v-if="enabledOptions['replyTo']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
          }"
          variant="icon"
          @click.stop="handleReplyTo"
        />
        <MenuItem
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click.stop="handleCopy"
        />
        <!-- Desabilitado, nao escondido: a janela de 15 min do WhatsApp e regra
             da plataforma e o usuario nao tem como adivinha-la. Sumir gera
             chamado ("o Editar quebrou"); desabilitado com o motivo ensina. -->
        <MenuItem
          v-if="enabledOptions['edit'] || enabledOptions['editDisabled']"
          :option="{
            icon: 'edit',
            label: $t('CONVERSATION.EDIT_MESSAGE.MENU'),
          }"
          variant="icon"
          :disabled="!enabledOptions['edit']"
          :reason="enabledOptions['editDisabledReason']"
          @click.stop="enabledOptions['edit'] && openEditModal()"
        />
        <MenuItem
          v-if="enabledOptions['translate']"
          :option="{
            icon: 'translate',
            label: $t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click.stop="handleTranslate"
        />
        <MenuItem
          v-if="enabledOptions['forward']"
          :option="{
            icon: 'share',
            label: $t('CONVERSATION.CONTEXT_MENU.FORWARD'),
          }"
          variant="icon"
          @click.stop="handleForward"
        />
        <MenuItem
          v-if="enabledOptions['select']"
          :option="{
            icon: 'checkbox-checked',
            label: $t('CONVERSATION.CONTEXT_MENU.SELECT'),
          }"
          variant="icon"
          @click.stop="handleSelect"
        />
        <hr class="advup-menu__filete" />
        <MenuItem
          v-if="enabledOptions['copyLink']"
          :option="{
            icon: 'link',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click.stop="copyLinkToMessage"
        />
        <MenuItem
          v-if="enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'),
          }"
          variant="icon"
          @click.stop="showCannedResponseModal"
        />
        <hr v-if="enabledOptions['delete']" class="advup-menu__filete" />
        <MenuItem
          v-if="enabledOptions['delete']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          perigo
          @click.stop="openDeleteModal"
        />
      </div>
    </ContextMenu>
  </div>
</template>

<style scoped>
.mcm-menu {
  min-width: 220px;
}

.mcm-menu .advup-menu__filete {
  border: 0;
}

.mcm-menu .advup-menu__filete:first-child {
  display: none;
}

.mcm-editar {
  width: min(560px, calc(100vw - 2rem));
}

.mcm-corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}

.mcm-rotulo {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mcm-conta {
  font-size: var(--txt-nota);
  color: rgb(var(--slate-10));
  font-variant-numeric: tabular-nums;
}

.mcm-texto {
  min-height: 10rem;
  resize: vertical;
}

.mcm-janela {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  margin: 0;
  font-size: var(--txt-nota);
  color: rgb(var(--amber-11));
}

.mcm-janela__ico {
  width: var(--ico-1);
  height: var(--ico-1);
  flex-shrink: 0;
}

.mcm-atalho {
  font-size: var(--txt-nota);
  color: rgb(var(--slate-10));
}

@media (max-width: 639px) {
  .mcm-atalho {
    display: none;
  }
}
</style>
